import { useCallback, useEffect, useMemo, useState } from "react";
import { Check, ChevronDown, ChevronRight, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import {
  isProductArchived,
  partitionProductCatalogOptions,
} from "@/lib/productCatalogOptions";
import type { ProductCatalogOption } from "@/types/ProductCatalog";

function filterProductsForSearch({
  products,
  search,
  selectedIds,
}: {
  products: ProductCatalogOption[];
  search: string;
  selectedIds: string[];
}): ProductCatalogOption[] {
  const q = search.trim().toLowerCase();
  if (!q) return products;
  return products.filter((p) => {
    if (selectedIds.includes(p._id)) return true;
    const label = p.name?.trim() || "Untitled";
    return label.toLowerCase().includes(q);
  });
}

interface ProductCatalogMultiPickerProps {
  value: string[];
  products: ProductCatalogOption[];
  onChange: (productIds: string[]) => void;
  excludeProductId?: string;
  disabled?: boolean;
  placeholder?: string;
}

export function ProductCatalogMultiPicker({
  value,
  products,
  onChange,
  excludeProductId,
  disabled,
  placeholder = "Search products…",
}: ProductCatalogMultiPickerProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const selectedIds = useMemo(
    () => [...new Set(value.map((id) => id.trim()).filter(Boolean))],
    [value],
  );
  const [archivedOpen, setArchivedOpen] = useState(false);

  const selectableProducts = useMemo(
    () =>
      excludeProductId
        ? products.filter((entry) => entry._id !== excludeProductId)
        : products,
    [excludeProductId, products],
  );

  const filteredProducts = useMemo(
    () =>
      filterProductsForSearch({
        products: selectableProducts,
        search: query,
        selectedIds,
      }),
    [query, selectableProducts, selectedIds],
  );

  const { active, archived } = useMemo(
    () => partitionProductCatalogOptions(filteredProducts),
    [filteredProducts],
  );

  const selectedProducts = useMemo(
    () =>
      selectedIds
        .map((id) => products.find((p) => p._id === id))
        .filter((p): p is ProductCatalogOption => p != null),
    [products, selectedIds],
  );

  useEffect(() => {
    if (selectedProducts.some((p) => isProductArchived({ product: p }))) {
      setArchivedOpen(true);
    }
  }, [selectedProducts]);

  const handleToggle = useCallback(
    ({ productId, checked }: { productId: string; checked: boolean }) => {
      const next = checked
        ? [...new Set([...selectedIds, productId])]
        : selectedIds.filter((id) => id !== productId);
      onChange(next);
    },
    [onChange, selectedIds],
  );

  const productLabel = (product: ProductCatalogOption) =>
    product.name?.trim() || "Untitled";

  const showEmpty =
    query.trim().length > 0 && active.length === 0 && archived.length === 0;

  return (
    <div className="space-y-2">
      {selectedProducts.length > 0 ? (
        <div className="flex flex-wrap gap-2">
          {selectedProducts.map((product) => (
            <span
              key={product._id}
              className="inline-flex items-center gap-1 rounded-md border bg-muted px-2 py-1 text-sm"
            >
              <span>{productLabel(product)}</span>
              <button
                type="button"
                className="rounded-sm opacity-70 hover:opacity-100"
                disabled={disabled}
                onClick={() =>
                  handleToggle({ productId: product._id, checked: false })
                }
                aria-label={`Remove ${productLabel(product)}`}
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </span>
          ))}
        </div>
      ) : null}

      <Popover
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          if (!next) setQuery("");
        }}
      >
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            className="w-full justify-start font-normal text-muted-foreground"
            disabled={disabled}
          >
            {placeholder}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
          <div className="border-b p-2">
            <Input
              placeholder="Search products…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              autoFocus
            />
          </div>
          <div className="max-h-60 overflow-y-auto p-1">
            {selectableProducts.length === 0 ? (
              <p className="px-2 py-2 text-sm text-muted-foreground">
                No other products found — create products first.
              </p>
            ) : showEmpty ? (
              <p className="px-2 py-2 text-sm text-muted-foreground">No products found</p>
            ) : (
              <>
                {active.map((product) => {
                  const isSelected = selectedIds.includes(product._id);
                  return (
                    <button
                      key={product._id}
                      type="button"
                      className={cn(
                        "flex w-full items-center gap-2 rounded-sm px-2 py-2 text-left text-sm hover:bg-accent",
                        isSelected && "bg-accent",
                      )}
                      disabled={disabled}
                      onClick={() =>
                        handleToggle({
                          productId: product._id,
                          checked: !isSelected,
                        })
                      }
                    >
                      <Check
                        className={cn(
                          "h-4 w-4 shrink-0",
                          isSelected ? "opacity-100" : "opacity-0",
                        )}
                      />
                      <span className="truncate">{productLabel(product)}</span>
                    </button>
                  );
                })}
                {archived.length > 0 ? (
                  <>
                    <div className="my-1 border-t" />
                    <button
                      type="button"
                      className="flex w-full items-center gap-1 rounded-sm px-2 py-1.5 text-xs text-muted-foreground hover:bg-accent"
                      onClick={() => setArchivedOpen((current) => !current)}
                    >
                      {archivedOpen ? (
                        <ChevronDown className="h-3 w-3 shrink-0" />
                      ) : (
                        <ChevronRight className="h-3 w-3 shrink-0" />
                      )}
                      Archived ({archived.length})
                    </button>
                    {archivedOpen
                      ? archived.map((product) => {
                          const isSelected = selectedIds.includes(product._id);
                          return (
                            <button
                              key={product._id}
                              type="button"
                              className={cn(
                                "flex w-full items-center gap-2 rounded-sm px-2 py-2 text-left text-sm hover:bg-accent",
                                isSelected && "bg-accent",
                              )}
                              disabled={disabled}
                              onClick={() =>
                                handleToggle({
                                  productId: product._id,
                                  checked: !isSelected,
                                })
                              }
                            >
                              <Check
                                className={cn(
                                  "h-4 w-4 shrink-0",
                                  isSelected ? "opacity-100" : "opacity-0",
                                )}
                              />
                              <span className="truncate">{productLabel(product)}</span>
                            </button>
                          );
                        })
                      : null}
                  </>
                ) : null}
              </>
            )}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
